import { type ChangeEvent, type FocusEvent, useContext, useEffect, useState } from 'react';

import { Input } from '@/components/Input/Input';
// Components
import { ActionTypes, type Address } from '@/components/Store.d';
import { Context } from '@/components/Store';
import { getKeyByValue } from '@/lib/util';
import { getStreet, getZipCode } from '@/lib/http';
import { InputLabel } from '@/components/InputLabel/InputLabel';
import { languageStrings } from '@/l18n/de';
import { Select } from '@/components/Select/Select';
import { titleList } from '@/lib/config';
import { validateInput } from '@/lib/validator';

/**
 * AddressFields component for address input
 * @returns {React.ReactElement}
 */
const AddressFields = () => {
  const [state, dispatch] = useContext(Context);
  const [zipCityValue, setZipCityValue] = useState(
    `${state?.address?.delivery?.zip && state?.address?.delivery?.city ? `${state?.address?.delivery?.zip} ${state?.address?.delivery?.city}` : ''}`
  );

  useEffect(() => {
    void getZipCode('0000').catch(() => {
      // It's okay for the warmup call to fail
    });
  }, []);

  const removeDropdown = (type?: 'streets' | 'zipCodes') => {
    dispatch({
      type: ActionTypes.resetCompletions,
      payload: {
        key: type?.length ? `autocompletions.${type}` : 'autocompletions',
        value: type?.length ? [] : {}
      }
    });
  };

  return (
    <>
      <Select
        defaultValue={state?.address?.delivery?.title ? getKeyByValue(titleList, state.address.delivery.title) : ''}
        id="titel"
        labelText={languageStrings.forms.titleList.label}
        onChange={(event: ChangeEvent<HTMLSelectElement>) => {
          dispatch({
            type: ActionTypes.addressChange,
            payload: {
              key: 'address.delivery.title',
              value: titleList[event.target.value as keyof typeof titleList]
            }
          });
        }}
        onFocus={() => removeDropdown()}
        options={titleList}
      />

      <div className="form--text-compact">
        <InputLabel
          autoComplete="on"
          data-validation-error={languageStrings.forms.input.validationError.firstName}
          defaultValue={state?.address?.delivery?.firstName}
          id={'deliveryFirstName'}
          labelText={languageStrings.forms.input.label.firstName}
          name={'deliveryFirstName'}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            if (validateInput(event.target)) {
              dispatch({
                type: ActionTypes.addressChange,
                payload: {
                  key: 'address.delivery.firstName',
                  value: event.target.value
                }
              });
            }
          }}
          onFocus={() => removeDropdown()}
          pattern="^[A-Za-zÀ-ž\-\. ]{2,}$"
          placeholder={languageStrings.forms.input.label.firstName}
          required={true}
          type="text"
        />
      </div>

      <div className="form--text-compact">
        <InputLabel
          autoComplete="on"
          data-validation-error={languageStrings.forms.input.validationError.lastName}
          defaultValue={state?.address?.delivery?.lastName}
          id={'deliveryLastName'}
          labelText={languageStrings.forms.input.label.lastName}
          name={'deliveryLastName'}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            if (validateInput(event.target)) {
              dispatch({
                type: ActionTypes.addressChange,
                payload: {
                  key: 'address.delivery.lastName',
                  value: event.target.value
                }
              });
            }
          }}
          onFocus={() => removeDropdown()}
          pattern="^[A-Za-zÀ-ž\-\. ]{2,}$"
          placeholder={languageStrings.forms.input.label.lastName}
          required={true}
          type="text"
        />
      </div>

      <div className="form--text-compact">
        <InputLabel
          autoComplete={
            /* https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#Preventing_autofilling_with_autocompletenew-password */
            'new-password'
          }
          data-validation-error={languageStrings.forms.input.validationError.zipCity}
          dropdown="zipCodes"
          enterKeyHint="search"
          id={'deliveryZipCity'}
          labelText={languageStrings.forms.input.label.zipCity}
          list={'deliveryZipCityList'}
          name={'deliveryZipCity'}
          onBlur={(event: FocusEvent<HTMLInputElement>) => {
            const inputValue = event.target.value;
            setZipCityValue(`${state?.address?.delivery?.zip || ''} ${state?.address?.delivery?.city || ''}`);

            if (inputValue.length > 1) {
              void getZipCode(inputValue.substring(0, 3))
                .then((results) => {
                  if (!results.length || results.every((addr) => String(addr.name ?? '').toLowerCase() !== inputValue.substring(5).toLowerCase())) {
                    event.target.setCustomValidity(event.target.dataset.validationError ?? '');
                  } else {
                    event.target.setCustomValidity('');
                  }
                })
                .catch(() => {
                  // Network error - keep existing validation state
                });
            }
          }}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            event.persist();

            const inputValue = event.target.value;
            setZipCityValue(inputValue);

            dispatch({
              type: ActionTypes.zipCity,
              payload: {
                zip: inputValue.length > 4 ? inputValue.substring(0, 4) : inputValue,
                city: inputValue.length > 4 ? inputValue.substring(5) : ''
              }
            });

            if (inputValue.length > 1) {
              void getZipCode(inputValue)
                .then((results) => {
                  if (results.length > 0) {
                    dispatch({
                      type: ActionTypes.addressVerification,
                      payload: {
                        key: 'autocompletions.zipCodes',
                        value: results
                      }
                    });
                  }
                })
                .catch(() => {
                  // Silently handle error - autocomplete will just not appear
                });
            }
          }}
          onFocus={() => {
            removeDropdown('streets');
          }}
          onKeyDown={(event: KeyboardEvent) => {
            if (event.key === 'Escape') {
              removeDropdown('zipCodes');
            }
          }}
          pattern="^[1-9]{1}[\d]{3} [A-ZÀ-Ž]{1}[a-zA-ZÀ-ž\-\. ]{1,}$"
          placeholder={languageStrings.forms.input.label.zipCity}
          required={true}
          type="text"
          value={zipCityValue}
        />
      </div>

      <div className="form--text-compact twothirds">
        <InputLabel
          autoComplete={
            /* https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#Preventing_autofilling_with_autocompletenew-password */
            'new-password'
          }
          disabled={state?.address?.delivery?.zip?.length && state?.address?.delivery?.city?.length ? false : true}
          dropdown="streets"
          enterKeyHint="search"
          id={'deliveryStreet'}
          labelText={languageStrings.forms.input.label.street}
          list={'deliveryStreetList'}
          minLength="2"
          name={'deliveryStreet'}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            event.persist();

            dispatch({
              type: ActionTypes.addressChange,
              payload: {
                key: 'address.delivery.street',
                value: event.target.value
              }
            });

            if (state?.autocompletions?.streets) {
              const streetNames = state.autocompletions.streets.map((item) => item.name);

              if (!streetNames.includes(event.target.value)) {
                dispatch({
                  type: ActionTypes.setStreetId,
                  payload: {
                    key: 'address.delivery.streetID',
                    value: ''
                  }
                });
              } else {
                const match = state.autocompletions.streets.filter((item) => item.name === event.target.value)[0];

                if (match) {
                  dispatch({
                    type: ActionTypes.setStreetId,
                    payload: {
                      key: 'address.delivery.streetID',
                      value: match.streetNumber
                    }
                  });
                }
              }
            }

            if (event.target.value.length > 2 && state?.address?.delivery) {
              const addressObject = {
                delivery: {
                  ...state.address.delivery,
                  streetPart: event.target.value
                }
              } as Address;

              void getStreet(addressObject)
                .then((results) => {
                  if (results.length > 0) {
                    dispatch({
                      type: ActionTypes.addressVerification,
                      payload: {
                        key: 'autocompletions.streets',
                        value: results
                      }
                    });
                  } else {
                    removeDropdown('streets');
                  }
                })
                .catch(() => {
                  // Network error - clear any existing autocomplete dropdown
                  removeDropdown('streets');
                });
            }
          }}
          onKeyDown={(event: KeyboardEvent) => {
            if (event.key === 'Escape') {
              removeDropdown('streets');
            }
          }}
          placeholder={languageStrings.forms.input.label.street}
          required={true}
          type="text"
          value={state?.address?.delivery?.street}
        />
      </div>

      <Input
        defaultValue={state?.address?.delivery?.streetID}
        id="streetID"
        name="streetID"
        type="hidden"
      />

      <div className="form--text-compact onethird">
        <InputLabel
          data-validation-error={languageStrings.forms.input.validationError.houseNumber}
          defaultValue={state?.address?.delivery?.houseNumber}
          id={'deliveryHouseNumber'}
          labelText={languageStrings.forms.input.label.houseNumber}
          name={'deliveryHouseNumber'}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            validateInput(event.target);

            dispatch({
              type: ActionTypes.addressChange,
              payload: {
                key: 'address.delivery.houseNumber',
                value: event.target.value
              }
            });
          }}
          onFocus={() => removeDropdown()}
          pattern="^[\w+-\/\\]{1,16}$"
          placeholder={languageStrings.forms.input.label.houseNumber}
          required={true}
          type="text"
        />
      </div>

      <div className="form--text-compact onethird">
        <InputLabel
          id={'deliveryStairs'}
          labelText={languageStrings.forms.input.label.stairs}
          min="0"
          name={'deliveryStairs'}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            validateInput(event.target);

            dispatch({
              type: ActionTypes.addressChange,
              payload: {
                key: 'address.delivery.stairs',
                value: event.target.value
              }
            });
          }}
          onFocus={() => removeDropdown()}
          placeholder={languageStrings.forms.input.label.stairs}
          type="text"
          value={state?.address?.delivery?.stairs}
        />
      </div>

      <div className="form--text-compact onethird">
        <InputLabel
          id={'deliveryFloor'}
          labelText={languageStrings.forms.input.label.floor}
          name={'deliveryFloor'}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            validateInput(event.target);

            dispatch({
              type: ActionTypes.addressChange,
              payload: {
                key: 'address.delivery.floor',
                value: event.target.value
              }
            });
          }}
          onFocus={() => removeDropdown()}
          placeholder={languageStrings.forms.input.label.floor}
          type="text"
          value={state?.address?.delivery?.floor}
        />
      </div>

      <div className="form--text-compact onethird">
        <InputLabel
          data-validation-error={languageStrings.forms.input.validationError.doorNumber}
          id={'deliveryDoor'}
          labelText={languageStrings.forms.input.label.doorNumber}
          minLength="1"
          name={'deliveryDoor'}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            validateInput(event.target);

            dispatch({
              type: ActionTypes.addressChange,
              payload: {
                key: 'address.delivery.door',
                value: event.target.value
              }
            });
          }}
          onFocus={() => removeDropdown()}
          placeholder={languageStrings.forms.input.label.doorNumber}
          type="text"
          value={state?.address?.delivery?.door}
        />
      </div>
    </>
  );
};

export { AddressFields };
